import { useLocalePath } from "@commercekit/remix";
import { useSubmit } from "@remix-run/react";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import useTrackNavigation from "./useTrackNavigation";

// Types
interface CheckoutAbandonmentData {
  [key: string]: string | number | boolean;
}

interface CheckoutAbandonmentContextType {
  setAbandonData: (data: CheckoutAbandonmentData | null) => void;
  clearAbandonData: () => void;
  setPaymentFlow: (isPayment: boolean) => void;
  handlePaymentReturnAbandonment: () => void;
  resetAbandonmentTracking: () => void;
  clearPaymentFlow: () => void;
  clearAllAbandonmentData: () => void;
  beaconData: string | null;
}

// Context
const CheckoutAbandonmentContext = createContext<
  CheckoutAbandonmentContextType | undefined
>(undefined);

// Provider Component
export function CheckoutAbandonmentProvider({
  children,
}: {
  children: ReactNode;
}) {
  const submit = useSubmit();

  const localePath = useLocalePath();
  const [beaconData, setBeaconData] = useState<string | null>(null);

  const setAbandonData = (data: CheckoutAbandonmentData | null) => {
    if (data) {
      // Prepare beacon data as JSON string immediately
      const jsonData = JSON.stringify(data);
      setBeaconData(jsonData);

      // Store in sessionStorage as backup
      sessionStorage.setItem("abandonCartParams", jsonData);
    } else {
      setBeaconData(null);
      sessionStorage.removeItem("abandonCartParams");
    }
  };

  const clearAbandonData = () => {
    setAbandonData(null);
  };

  const setPaymentFlow = (isPayment: boolean) => {
    if (isPayment) {
      sessionStorage.setItem("isPaymentFlow", "true");

      // Store current abandon data for potential return abandonment
      const currentAbandonData = sessionStorage.getItem("abandonCartParams");
      if (currentAbandonData) {
        sessionStorage.setItem("paymentReturnAbandonData", currentAbandonData);
      }
    } else {
      sessionStorage.removeItem("isPaymentFlow");
      sessionStorage.removeItem("paymentReturnAbandonData");
    }
  };

  // Restore data from sessionStorage on mount (in case of page refresh)
  useEffect(() => {
    const storedParams = sessionStorage.getItem("abandonCartParams");

    if (storedParams) {
      try {
        // storedParams is already a JSON string, so just set it directly
        setBeaconData(storedParams);
      } catch (error) {
        console.error("Failed to restore checkout abandonment data:", error);
        clearAbandonData();
      }
    }
  }, []);

  const handlePaymentReturnAbandonment = () => {
    const alreadyAbandoned = sessionStorage.getItem("cartAbandoned");
    const paymentReturnData = sessionStorage.getItem(
      "paymentReturnAbandonData"
    );
    if (!alreadyAbandoned && paymentReturnData) {
      // Send beacon with the stored data

      submit(paymentReturnData, {
        action: "/checkout",
        navigate: false,
        method: "post",
      });

      // Mark as abandoned and clean up
      sessionStorage.setItem("cartAbandoned", "true");
      clearPaymentFlow(); // Add this - abandonment tracked, clean up
      const orderId = sessionStorage.getItem("orderId");
      // window.location.replace(
      //   localePath(`/thank-you?orderRefId=${orderId}&orderStatusId=-90`)
      // );
    }
  };

  const resetAbandonmentTracking = () => {
    sessionStorage.removeItem("cartAbandoned");
    sessionStorage.removeItem("abandonCartParams");

    // No need to reset cartAbandoned.current - it resets automatically on component remount
    clearAbandonData(); // Clear context beacon data
  };

  const clearPaymentFlow = () => {
    // Only called when payment is truly cancelled/completed
    sessionStorage.removeItem("paymentReturnAbandonData");
    sessionStorage.removeItem("isPaymentFlow");
  };

  const clearAllAbandonmentData = () => {
    // Nuclear option - clear everything
    resetAbandonmentTracking();
    clearPaymentFlow();
  };

  return (
    <CheckoutAbandonmentContext.Provider
      value={{
        setAbandonData,
        clearAbandonData,
        setPaymentFlow,
        handlePaymentReturnAbandonment,
        resetAbandonmentTracking,
        clearPaymentFlow,
        clearAllAbandonmentData,
        beaconData,
      }}
    >
      {children}
    </CheckoutAbandonmentContext.Provider>
  );
}

// Hook to use the context
export function useCheckoutAbandonment() {
  const context = useContext(CheckoutAbandonmentContext);
  if (context === undefined) {
    if (typeof window !== "undefined") {
      console.warn(
        "CheckoutAbandonment context accessed after provider unmounted"
      );
    }
    return {
      setAbandonData: () => {},
      clearAbandonData: () => {},
      setPaymentFlow: () => {},
      handlePaymentReturnAbandonment: () => {},
      resetAbandonmentTracking: () => {},
      clearPaymentFlow: () => {},
      clearAllAbandonmentData: () => {},
      beaconData: null,
    };
  }
  return context;
}

// Hook for Layout to handle navigation tracking - only tracks checkout abandonment
export function useLayoutCheckoutTracking() {
  const submit = useSubmit();
  const { beaconData, clearAbandonData } = useCheckoutAbandonment();

  useTrackNavigation((fromRoute, toRoute) => {
    // Only track if we have beacon data and we're leaving checkout
    if (beaconData && fromRoute === "/checkout") {
      // Check if abandonment already happened via timeout
      const alreadyAbandoned = sessionStorage.getItem("cartAbandoned");
      if (alreadyAbandoned) {
        clearAbandonData();
        return;
      }

      if (toRoute === "external") {
        const isPaymentFlow = sessionStorage.getItem("isPaymentFlow");
        sessionStorage.removeItem("isPaymentFlow");

        if (!isPaymentFlow) {
          // True abandonment - send beacon with pre-prepared JSON data

          fetch("/checkout", {
            method: "POST",
            body: beaconData,
            headers: {
              "Content-Type": "application/json",
            },
            keepalive: true, // Critical for external navigation
          });
          sessionStorage.setItem("cartAbandoned", "true");
        }
      } else {
        // Internal navigation away from checkout - use regular submit

        submit(beaconData, {
          action: "/checkout",
          navigate: false,
          method: "post",
        });
        sessionStorage.setItem("cartAbandoned", "true");
      }

      // Clear the data after tracking
      clearAbandonData();
    }
  });
}
